import _ from 'lodash';
import reduceReducers from 'reduce-reducers';
import update from 'immutability-helper';
import { validateSchema, isStringEmpty } from '../common/validations';
import qwPartners from '../common/clients/qwPartners';
import I18n from '../../config/i18n';
import {
  formatTerritories,
  constructServiceAreasFromTrex,
} from '../common/utils/region';
import { sortServiceAreaByDays } from '../common/utils/helpers';
import { showAppBusy, handleAppError } from './app';
import { loadApprovals } from './approvals';
import { receiveRegion } from './regions';

const initialState = {
  serviceAreas: [],
  theatreLists: [],
  errors: {},
  theatreListErrors: {},
  receivedAt: Date.now(),
  registeredServiceAreas: [],
  registeredTheatreLists: [],
};
const RECEIVE = 'serviceAreas/RECEIVE';
const ADD = 'serviceAreas/ADD';
const UPDATE = 'serviceAreas/UPDATE';
const DELETE = 'serviceAreas/DELETE';
const DELETE_SERVICE_AREAS = 'serviceAreas/DELETE_SERVICE_AREAS';
const RESET_SERVICE_AREAS = 'serviceAreas/RESET_SERVICE_AREAS';
const ADD_EXCEPTION = 'serviceAreas/ADD_EXCEPTION';
const DELETE_EXCEPTION = 'serviceAreas/DELETE_EXCEPTION';
const ADD_DELIVERY = 'serviceAreas/ADD_DELIVERY';
const UPDATE_DELIVERY = 'serviceAreas/UPDATE_DELIVERY';
const DELETE_DELIVERY = 'serviceAreas/DELETE_DELIVERY';
const SCHEMA_ID = 'http://partners.qubewire.com/schemas/updateServiceAreas';
const THEATRE_LISTS_SCHEMA_ID =
  'http://partners.qubewire.com/schemas/updateTheatreLists';

const RECEIVE_THEATRE_LIST = 'serviceAreas/RECEIVE_THEATRE_LIST';
const RESET_THEATRE_LIST = 'serviceAreas/RESET_THEATRE_LIST';
const ADD_THEATRE_LIST = 'serviceAreas/ADD_THEATRE_LIST';
const DELETE_THEATRE_LIST = 'serviceAreas/DELETE_THEATRE_LIST';
const ADD_THEATRES_TO_LIST = 'serviceAreas/ADD_THEATRES_TO_LIST';
const ADD_THEATRE_DELIVERY = 'serviceAreas/ADD_THEATRE_DELIVERY';
const UPDATE_THEATRE_DELIVERY = 'serviceAreas/UPDATE_THEATRE_DELIVERY';
const DELETE_THEATRE_DELIVERY = 'serviceAreas/DELETE_THEATRE_DELIVERY';
const DELETE_THEATRE_FROM_LISTS = 'serviceAreas/DELETE_THEATRE_FROM_LISTS';
const UPDATE_THEATRE_DELIVERY_TYPE =
  'serviceAreas/UPDATE_THEATRE_DELIVERY_TYPE';

const ACCOUNT_FIELDS = ['serviceAreas'];

export function isDeliveryOptionEmpty(deliveryOption) {
  return (
    deliveryOption &&
    isStringEmpty(deliveryOption.max) &&
    isStringEmpty(deliveryOption.unit) &&
    isStringEmpty(deliveryOption.price) &&
    isStringEmpty(deliveryOption.shippingPickupTime)
  );
}

export function isTheatreDeliveryOptionEmpty(deliveryOption) {
  return (
    deliveryOption &&
    isStringEmpty(deliveryOption.time) &&
    isStringEmpty(deliveryOption.unit) &&
    isStringEmpty(deliveryOption.price) &&
    isStringEmpty(deliveryOption.size)
  );
}

export function receiveServiceAreas(account, isPartner) {
  let serviceAreas = _.pick(account, ACCOUNT_FIELDS);
  serviceAreas = constructServiceAreasFromTrex(
    serviceAreas.serviceAreas.filter(
      (serviceArea) => serviceArea.validIn.countries,
    ),
  );
  formatServiceAreas(serviceAreas, isPartner);

  const serviceAreasValue = serviceAreas.map((serviceArea) =>
    _.extend(serviceArea, {
      deliveryOptions: sortServiceAreaByDays(serviceArea.deliveryOptions),
    }),
  );

  const registeredServiceAreas = _.cloneDeep(serviceAreasValue);

  return {
    type: RECEIVE,
    payload: {
      serviceAreas: serviceAreasValue,
      registeredServiceAreas: registeredServiceAreas,
    },
  };
}

export function receiveTheatreLists(account, isPartner) {
  let theatreLists = _.pick(account, ACCOUNT_FIELDS);
  theatreLists = constructTheatreLists(
    theatreLists.serviceAreas.filter(
      (serviceArea) => serviceArea.validIn.theatreList,
    ),
    isPartner,
  );

  return { type: RECEIVE_THEATRE_LIST, payload: theatreLists };
}

function constructTheatreLists(theatreLists, isPartner = false) {
  return theatreLists.map((theatreList) => {
    return {
      id: theatreList.id,
      deliveryType: theatreList.deliveryType,
      theatres: theatreList.validIn.theatreList.theatres,
      deliveryOptions: theatreList.validIn.theatreList.deliveryOptions.map(
        (deliveryOption) => {
          deliveryOption.time = deliveryOption.deliveryTime.value;
          deliveryOption.unit = deliveryOption.deliveryTime.unit;
          deliveryOption.price = isPartner
            ? deliveryOption.price / 100
            : deliveryOption.price;
          return deliveryOption;
        },
      ),
    };
  });
}

function formatServiceAreas(serviceAreas, isPartner = false) {
  (serviceAreas || []).forEach((serviceArea) =>
    _.extend(serviceArea, {
      deliveryOptions: serviceArea.deliveryOptions.map((deliveryOption) =>
        _.extend(deliveryOption, {
          max: !_.isEmpty(deliveryOption.maxTime)
            ? deliveryOption.maxTime.value
            : '',
          unit: !_.isEmpty(deliveryOption.maxTime)
            ? deliveryOption.maxTime.unit
            : '',
          price: _.isNumber(deliveryOption.price)
            ? isPartner
              ? deliveryOption.price / 100
              : deliveryOption.price
            : 0,
        }),
      ),
    }),
  );
}

export function addServiceArea(payload) {
  return (dispatch) => {
    if (payload.region) {
      dispatch(receiveRegion(payload.region));
      payload = _.omit(payload, 'region');
    }
    return dispatch({ type: ADD, payload });
  };
}

export function addTheatreList(payload) {
  return (dispatch) => {
    return dispatch({ type: ADD_THEATRE_LIST, payload });
  };
}

export function addTheatresToList(payload, theatreListIndex) {
  return (dispatch) => {
    return dispatch({
      type: ADD_THEATRES_TO_LIST,
      payload,
      meta: { theatreListIndex },
    });
  };
}

export function updateServiceArea(payload, meta) {
  return { type: UPDATE, payload, meta };
}

export function resetServiceAreas(payload) {
  return { type: RESET_SERVICE_AREAS, payload };
}

export function resetTheatreLists(payload) {
  return { type: RESET_THEATRE_LIST, payload };
}

export function deleteServiceArea(meta) {
  return { type: DELETE, meta };
}

export function deleteTheatreList(meta) {
  return { type: DELETE_THEATRE_LIST, meta };
}

export function deleteServiceAreas() {
  return { type: DELETE_SERVICE_AREAS };
}

export function addServiceAreaException(serviceAreaIndex, code) {
  return { type: ADD_EXCEPTION, payload: code, meta: serviceAreaIndex };
}

export function deleteServiceAreaException(serviceAreaIndex, exceptionIndex) {
  return {
    type: DELETE_EXCEPTION,
    payload: null,
    meta: {
      serviceAreaIndex,
      exceptionIndex,
    },
  };
}

export function addServiceAreaDelivery(
  serviceAreaIndex,
  deliveryOptionIndex,
  deliveryOption,
) {
  if (isDeliveryOptionEmpty(deliveryOption)) {
    return { type: null };
  } else {
    return {
      type: ADD_DELIVERY,
      payload: deliveryOption,
      meta: { serviceAreaIndex },
    };
  }
}

export function addTheatreListDelivery(
  theatreListIndex,
  deliveryOptionIndex,
  deliveryOption,
) {
  if (isTheatreDeliveryOptionEmpty(deliveryOption)) {
    return { type: null };
  } else {
    return {
      type: ADD_THEATRE_DELIVERY,
      payload: deliveryOption,
      meta: { theatreListIndex },
    };
  }
}

export function updateServiceAreaDelivery(
  serviceAreaIndex,
  deliveryOptionIndex,
  deliveryOption,
) {
  if (isDeliveryOptionEmpty(deliveryOption)) {
    return deleteServiceAreaDelivery(serviceAreaIndex, deliveryOptionIndex);
  } else {
    return {
      type: UPDATE_DELIVERY,
      payload: deliveryOption,
      meta: {
        serviceAreaIndex,
        deliveryOptionIndex,
      },
    };
  }
}

export function updateTheatreListDelivery(
  theatreListIndex,
  deliveryOptionIndex,
  deliveryOption,
) {
  if (isTheatreDeliveryOptionEmpty(deliveryOption)) {
    return deleteTheatreListDelivery(theatreListIndex, deliveryOptionIndex);
  } else {
    return {
      type: UPDATE_THEATRE_DELIVERY,
      payload: deliveryOption,
      meta: {
        theatreListIndex,
        deliveryOptionIndex,
      },
    };
  }
}

export function updateTheatreListDeliveryType(theatreListIndex, deliveryType) {
  return {
    type: UPDATE_THEATRE_DELIVERY_TYPE,
    payload: deliveryType,
    meta: {
      theatreListIndex,
    },
  };
}

export function deleteServiceAreaDelivery(
  serviceAreaIndex,
  deliveryOptionIndex,
) {
  return {
    type: DELETE_DELIVERY,
    meta: {
      serviceAreaIndex,
      deliveryOptionIndex,
    },
  };
}

export function deleteTheatreListDelivery(
  theatreListIndex,
  deliveryOptionIndex,
) {
  return {
    type: DELETE_THEATRE_DELIVERY,
    meta: {
      theatreListIndex,
      deliveryOptionIndex,
    },
  };
}

export function deleteTheatreListTheatres(theatreIndex, theatreListIndex) {
  return {
    type: DELETE_THEATRE_FROM_LISTS,
    meta: {
      theatreListIndex,
      theatreIndex,
    },
  };
}

export function formatDeliveryOptions(serviceArea) {
  serviceArea.deliveryOptions.forEach((deliveryOption) => {
    if (deliveryOption) {
      deliveryOption.maxTime = {
        unit: deliveryOption.unit,
        value: deliveryOption.max,
      };
      delete deliveryOption.max;
      delete deliveryOption.unit;
      delete deliveryOption._clientId;
    } else if (!_.isEmpty(serviceArea.subdivisions)) {
      serviceArea.subdivisions.forEach(formatDeliveryOptions);
    }
  });
}

export function saveServiceAreas() {
  return (dispatch, getState) => {
    dispatch(showAppBusy(true));
    const state = getState();
    const serviceAreas = _.cloneDeep(state.serviceAreas.serviceAreas).map(
      (serviceArea) => {
        formatDeliveryOptions(serviceArea);
        return _.extend(
          formatTerritories([serviceArea], state.regions, serviceArea.id),
          {
            deliveryType: serviceArea.deliveryType,
          },
        );
      },
    );

    const temptTheatreList = state.serviceAreas.theatreLists;

    const theatreLists = formatTheatreLists(_.cloneDeep(temptTheatreList));

    const data = { serviceAreas: _.concat(serviceAreas, theatreLists) };

    return qwPartners
      .post('/v1/approvals?approvalType=service_areas', data)
      .then(() => {
        return dispatch(loadApprovals());
      })
      .then(() => {
        dispatch(showAppBusy(false));
      })
      .catch((error) => {
        dispatch(showAppBusy(false));
        dispatch(handleAppError(error, I18n.t('couldNotSubmitRegistration')));
      });
  };
}

export function formatTheatreLists(theatreLists) {
  return theatreLists.map((theatreList) => {
    theatreList.theatres = _.map(theatreList.theatres, 'id');
    formatTheatreDeliveryOptions(theatreList.deliveryOptions);
    return {
      id: theatreList.id,
      deliveryType: theatreList.deliveryType,
      validIn: {
        theatreList,
      },
    };
  });
}

export function formatTheatreDeliveryOptions(deliveryOptions) {
  deliveryOptions.forEach((deliveryOption) => {
    if (deliveryOption) {
      deliveryOption.deliveryTime = {
        unit: deliveryOption.unit,
        value: deliveryOption.time,
      };
      delete deliveryOption.time;
      delete deliveryOption.unit;
      delete deliveryOption._clientId;
    }
  });
}

function reducer(state = initialState, action) {
  const { type, payload, meta } = action;
  switch (type) {
    case RECEIVE:
      return Object.assign({}, initialState, payload, {
        receivedAt: Date.now(),
      });

    case ADD:
      if (_.find(state.serviceAreas, { code: payload.code })) {
        return state;
      } else {
        return update(state, { serviceAreas: { $push: [payload] } });
      }

    case UPDATE:
      return update(state, { serviceAreas: { $splice: [[meta, 1, payload]] } });

    case DELETE:
      return update(state, { serviceAreas: { $splice: [[meta, 1]] } });

    case ADD_EXCEPTION:
      return update(state, {
        serviceAreas: {
          [meta]: {
            excludeCodes: {
              $apply: (value) => [...(value || []), payload],
            },
          },
        },
      });

    case DELETE_EXCEPTION:
      return update(state, {
        serviceAreas: {
          [meta.serviceAreaIndex]: {
            excludeCodes: { $splice: [[meta.exceptionIndex, 1]] },
          },
        },
      });

    case ADD_DELIVERY:
      return update(state, {
        serviceAreas: {
          [meta.serviceAreaIndex]: {
            deliveryOptions: {
              $apply: (value) => [...(value || []), payload],
            },
          },
        },
      });

    case UPDATE_DELIVERY:
      return update(state, {
        serviceAreas: {
          [meta.serviceAreaIndex]: {
            deliveryOptions: {
              $splice: [[meta.deliveryOptionIndex, 1, payload]],
            },
          },
        },
      });

    case DELETE_DELIVERY:
      return update(state, {
        serviceAreas: {
          [meta.serviceAreaIndex]: {
            deliveryOptions: { $splice: [[meta.deliveryOptionIndex, 1]] },
          },
        },
      });

    case RESET_SERVICE_AREAS:
      return update(state, { serviceAreas: { $set: payload } });

    case DELETE_SERVICE_AREAS:
      return update(state, { $set: initialState });
    default:
      return state;

    case RECEIVE_THEATRE_LIST:
      return update(state, {
        theatreLists: { $set: payload },
        registeredTheatreLists: { $set: payload },
      });

    case RESET_THEATRE_LIST:
      return update(state, { theatreLists: { $set: payload } });

    case ADD_THEATRE_LIST:
      return update(state, { theatreLists: { $push: [payload] } });

    case DELETE_THEATRE_LIST:
      return update(state, { theatreLists: { $splice: [[meta, 1]] } });

    case ADD_THEATRES_TO_LIST:
      return update(state, {
        theatreLists: {
          [meta.theatreListIndex]: { theatres: { $set: payload } },
        },
      });

    case DELETE_THEATRE_FROM_LISTS:
      return update(state, {
        theatreLists: {
          [meta.theatreListIndex]: {
            theatres: { $splice: [[meta.theatreIndex, 1]] },
          },
        },
      });

    case ADD_THEATRE_DELIVERY:
      return update(state, {
        theatreLists: {
          [meta.theatreListIndex]: {
            deliveryOptions: {
              $apply: (value) => [...(value || []), payload],
            },
          },
        },
      });

    case UPDATE_THEATRE_DELIVERY:
      return update(state, {
        theatreLists: {
          [meta.theatreListIndex]: {
            deliveryOptions: {
              $splice: [[meta.deliveryOptionIndex, 1, payload]],
            },
          },
        },
      });

    case DELETE_THEATRE_DELIVERY:
      return update(state, {
        theatreLists: {
          [meta.theatreListIndex]: {
            deliveryOptions: { $splice: [[meta.deliveryOptionIndex, 1]] },
          },
        },
      });

    case UPDATE_THEATRE_DELIVERY_TYPE:
      return update(state, {
        theatreLists: {
          [meta.theatreListIndex]: { deliveryType: { $set: payload } },
        },
      });
  }
}

function validator(state, action) {
  const { type } = action;
  switch (type) {
    case RECEIVE:
    case ADD:
    case UPDATE:
    case DELETE:
    case ADD_EXCEPTION:
    case DELETE_EXCEPTION:
    case ADD_DELIVERY:
    case UPDATE_DELIVERY:
    case DELETE_DELIVERY: {
      const errors = validateSchema(state, SCHEMA_ID);
      return Object.assign({}, state, { errors });
    }
    case RECEIVE_THEATRE_LIST:
    case ADD_THEATRE_LIST:
    case DELETE_THEATRE_LIST:
    case ADD_THEATRES_TO_LIST:
    case ADD_THEATRE_DELIVERY:
    case UPDATE_THEATRE_DELIVERY:
    case DELETE_THEATRE_DELIVERY:
    case DELETE_THEATRE_FROM_LISTS:
    case UPDATE_THEATRE_DELIVERY_TYPE: {
      const theatreListErrors = validateSchema(state, THEATRE_LISTS_SCHEMA_ID);
      return Object.assign({}, state, { theatreListErrors });
    }
    default:
      return state;
  }
}

export default reduceReducers(reducer, validator);
